/**
 * Service worker registration and update detection, outside React.
 *
 * Relies on `service-worker.js` NOT calling skipWaiting() at install time: the
 * whole flow hangs on the new worker parking in the `waiting` state, so the
 * page can warn the user before the new version takes over. Activation is
 * triggered on demand through `applyAppUpdate`, and `clientsClaim()` in the
 * worker then produces the `controllerchange` that reloads the page.
 */

// `sw.js` is what InjectManifest emits (see .webpack/webpack.pwa.js, swDest).
// The full `process.env.PUBLIC_URL` path must stay written out below:
// DefinePlugin substitutes text, so destructuring would yield undefined.
const SW_URL = `${process.env.PUBLIC_URL || '/'}sw.js`;

// Captured at module load, before any activation can happen: it tells a real
// update apart from the very first install, where clientsClaim() also fires a
// `controllerchange` that must not reload the page.
const WAS_CONTROLLED_AT_LOAD = Boolean(
  navigator.serviceWorker && navigator.serviceWorker.controller
);

const listeners = new Set();
let waitingWorker = null;
let hasStarted = false;
let isReloading = false;

/** Whether the browser exposes the service worker API at all. */
const isSupported = () => 'serviceWorker' in navigator;

/** Whether a service worker should be registered in this environment. */
// Guarded on NODE_ENV rather than the hostname: a production build must
// register even when reached through localhost or a LAN IP, while `yarn dev`
// must never register, since precaching the dev bundle would break HMR.
const isEnabled = () => isSupported() && process.env.NODE_ENV === 'production';

/** Drops every worker registered on this origin. Never throws. */
const unregisterAll = () => {
  if (!navigator.serviceWorker.getRegistrations) return;

  navigator.serviceWorker
    .getRegistrations()
    .then(registrations =>
      Promise.all(registrations.map(registration => registration.unregister()))
    )
    .catch(error => console.error('[SW] Unregistration failed', error));
};

/** Records the waiting worker and tells every subscriber about it, once. */
const signalUpdate = worker => {
  if (!WAS_CONTROLLED_AT_LOAD || waitingWorker) return;

  waitingWorker = worker;
  listeners.forEach(listener => listener());
};

/** Follows an installing worker until it parks in the waiting state. */
const watchInstalling = worker => {
  worker.addEventListener('statechange', () => {
    if (worker.state === 'installed') signalUpdate(worker);
  });
};

/** Reports an update that was already waiting when this page loaded. */
const reportAlreadyWaiting = registration => {
  if (registration.waiting) signalUpdate(registration.waiting);
};

/** Reloads once the new worker takes control, but only if this page was warned. */
const handleControllerChange = () => {
  if (!waitingWorker || isReloading) return;

  isReloading = true;
  window.location.reload();
};

/** Wires the registration events that can reveal a pending new version. */
const watchRegistration = registration => {
  reportAlreadyWaiting(registration);

  registration.addEventListener('updatefound', () => {
    if (registration.installing) watchInstalling(registration.installing);
  });
};

/** Registers the service worker and starts watching for a new version. Idempotent. */
export const startAppUpdateWatcher = () => {
  if (hasStarted) return;

  hasStarted = true;
  // Dev and prod are both served from https://localhost:3000, the same origin.
  // A worker installed by a production run would otherwise survive there and
  // keep serving its precache over the dev server, silently killing HMR, so
  // anything left behind gets dropped instead.
  if (!isEnabled()) {
    if (isSupported()) unregisterAll();

    return;
  }

  navigator.serviceWorker.addEventListener(
    'controllerchange',
    handleControllerChange
  );
  navigator.serviceWorker
    .register(SW_URL)
    .then(watchRegistration)
    .catch(error => console.error('[SW] Registration failed', error));
};

/** Subscribes to "a new version is waiting", replaying a signal already missed. */
export const subscribeToAppUpdate = onUpdateAvailable => {
  listeners.add(onUpdateAvailable);
  if (waitingWorker) onUpdateAvailable();

  return () => listeners.delete(onUpdateAvailable);
};

/** Activates the waiting worker; handleControllerChange then reloads the page. */
export const applyAppUpdate = () => {
  if (!waitingWorker) return;

  waitingWorker.postMessage({ type: 'SKIP_WAITING' });
};
